import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import TradingActivityMarker from '../../../../shared/charts/TradingActivityMarker';
import { TradingActivitiesData } from '../../../portfolio/types/tradingActivity';
import {
  getPortfolioView,
  setPortfolioView,
  getAccountView,
  setAccountView,
  getTickerView,
  setTickerView,
} from '../../../../shared/utils/chartToggles';

type ChartType = 'ticker' | 'account' | 'portfolio';

interface PerformanceChartCardProps {
  // Chart type and data
  chartType: ChartType;
  chartData: Array<{ time: number; close?: number; basis?: number | null; percent?: number; dollar?: number }>;
  title: string;
  
  // Selection state
  selectedTicker: string | null;
  selectedAccountId: string | null;
  timeWindowKey: string;
  
  // View state (from selectors)
  view?: 'pct' | 'dollar' | 'dol1' | 'dol2';
  
  // Trading activities
  tickerActivities?: TradingActivitiesData;
  accountActivities?: TradingActivitiesData;
  portfolioActivities?: TradingActivitiesData;
  groupingMs: number;
  onActivityMarkerClick: (date: string, activities: any[]) => void;
  
  // Formatters
  formatTime: (timestamp: number) => string;
  
  // Current prices for ticker chart
  currentPrices?: Record<string, number | null>;
  
  // Loading state
  isFetching?: boolean;
  
  // Hide toggle for friend portfolio views
  hideToggle?: boolean;
  className?: string;
}

/**
 * PerformanceChartCard displays performance charts for ticker, account, or portfolio.
 * Supports three chart types with different visualizations and trading activity markers.
 */
export const PerformanceChartCard: React.FC<PerformanceChartCardProps> = ({
  chartType,
  chartData,
  title,
  selectedTicker,
  selectedAccountId,
  timeWindowKey,
  view,
  tickerActivities,
  accountActivities,
  portfolioActivities,
  groupingMs,
  onActivityMarkerClick,
  formatTime,
  currentPrices,
  isFetching = false,
  hideToggle = false,
  className = '',
}) => {
  // State to force re-render when toggle changes
  const [toggleUpdateKey, setToggleUpdateKey] = useState(0);

  // Get current view state based on chart type (as a value, not a function)
  const currentViewValue = useMemo(() => {
    // Reference toggleUpdateKey to force recalculation when toggle state changes
    // The value itself isn't used, but its change triggers recalculation of localStorage reads
    void toggleUpdateKey;
    
    if (chartType === 'ticker' && selectedAccountId && selectedTicker) {
      return getTickerView(selectedAccountId, selectedTicker);
    } else if (chartType === 'account' && selectedAccountId) {
      return getAccountView(selectedAccountId);
    } else if (chartType === 'portfolio') {
      return getPortfolioView();
    }
    return chartType === 'ticker' ? 'dol1' : 'pct';
  }, [chartType, selectedAccountId, selectedTicker, toggleUpdateKey]);

  // Listen for localStorage changes (for toggle state)
  useEffect(() => {
    const handleStorageChange = () => {
      // Force re-render by dispatching custom event
      window.dispatchEvent(new Event('toggleStateChanged'));
    };
    const handleToggleStateChange = () => {
      // Force re-render by updating state
      setToggleUpdateKey(prev => prev + 1);
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('toggleStateChanged', handleToggleStateChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('toggleStateChanged', handleToggleStateChange);
    };
  }, []);

  // Toggle handler based on chart type
  const handleToggle = useCallback(() => {
    if (chartType === 'ticker' && selectedAccountId && selectedTicker) {
      const current = getTickerView(selectedAccountId, selectedTicker);
      const nextView = current === 'dol2' ? 'dol1' : 'dol2';
      setTickerView(selectedAccountId, selectedTicker, nextView);
      // Trigger re-render via custom event
      window.dispatchEvent(new Event('toggleStateChanged'));
    } else if (chartType === 'account' && selectedAccountId) {
      const current = getAccountView(selectedAccountId);
      const nextView = current === 'dollar' ? 'pct' : 'dollar';
      setAccountView(selectedAccountId, nextView);
      // Trigger re-render via custom event
      window.dispatchEvent(new Event('toggleStateChanged'));
    } else if (chartType === 'portfolio') {
      const current = getPortfolioView();
      const nextView = current === 'dollar' ? 'pct' : 'dollar';
      setPortfolioView(nextView);
      // Trigger re-render via custom event
      window.dispatchEvent(new Event('toggleStateChanged'));
    }
  }, [chartType, selectedAccountId, selectedTicker]);

  // Get toggle button image based on chart type and current view
  const toggleImage = useMemo(() => {
    const view = currentViewValue;
    if (chartType === 'ticker') {
      // Ticker charts: dol1 (price) vs dol2 (cumulative PnL)
      // Show the current view icon (dol1 by default)
      return view === 'dol2' ? '/line-toggle-dol2-lrg.png' : '/line-toggle-dol1-lrg.png';
    } else {
      // Account/Portfolio charts: pct vs dollar
      // Show line-toggle-dol-lrg.png when $ view is selected, line-toggle-pct-lrg.png when % view is selected
      return view === 'dollar' ? '/line-toggle-dol-lrg.png' : '/line-toggle-pct-lrg.png';
    }
  }, [chartType, currentViewValue]);

  // Get toggle button alt text
  const toggleAltText = useMemo(() => {
    const view = currentViewValue;
    if (chartType === 'ticker') {
      return view === 'dol2' ? 'Price view (dol1)' : 'Cumulative PnL view (dol2)';
    } else {
      return view === 'dollar' ? 'Percentage view (%)' : 'Dollar view ($)';
    }
  }, [chartType, currentViewValue]);
  // Helper to format dollar values with abbreviations (k for thousands, M for millions)
  const formatDollarAxis = useCallback((value: number): string => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (absValue >= 1000000) {
      // Millions
      const millions = absValue / 1000000;
      return `${sign}$${millions.toFixed(1)}M`;
    } else if (absValue >= 1000) {
      // Thousands
      const thousands = absValue / 1000;
      return `${sign}$${thousands.toFixed(1)}k`;
    } else {
      // Less than 1000
      return `${sign}$${absValue.toFixed(0)}`;
    }
  }, []);

  const renderTickerChart = () => {
    if (isFetching && chartData.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full space-y-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <span className="text-sm text-gray-500">Loading data...</span>
        </div>
      );
    }
    if (chartData.length === 0) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          No historical data available for the selected period.
        </div>
      );
    }

    const activitiesData = selectedAccountId
      ? (accountActivities || {})
      : (portfolioActivities || {});

    const currentView = view || 'dol1';
    const isDol2View = currentView === 'dol2';

    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
          <XAxis
            type="number"
            dataKey="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={formatTime}
            axisLine={false}
            tickLine={false}
            stroke="#6B7280"
          />
          <YAxis 
            domain={['auto', 'auto']} 
            axisLine={false} 
            tickLine={false} 
            stroke="#6B7280" 
            width={60}
            tickFormatter={formatDollarAxis}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              border: '1px solid #ccc',
              borderRadius: '4px',
            }}
            itemStyle={{ color: '#333' }}
            labelFormatter={(timestamp) => {
              const date = new Date(timestamp);
              const month = date.toLocaleString('default', { month: 'short' });
              const day = date.getDate();
              if (timeWindowKey === '1y' || timeWindowKey === '2y') {
                const year = date.getFullYear();
                return `${month} ${day}, ${year}`;
              }
              return `${month} ${day}`;
            }}
            formatter={(value: number, name: string, props: any) => {
              const pointData = props.payload;
              
              // Helper to format dollar values with commas
              const formatDollar = (val: number): string => {
                return val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              };
              
              if (isDol2View) {
                // dol2 view: Show cumulative PnL only for the main line ("Cumulative PnL")
                // Filter out cost basis and trading activity lines (which have empty or different names)
                if (name !== 'Cumulative PnL') {
                  return [null, null];
                }
                const formattedValue = `${value >= 0 ? '+' : '-'}$${formatDollar(Math.abs(value))}`;
                return [formattedValue, `${selectedTicker || 'Ticker'} Profit/Loss`];
              } else {
                // dol1 view: Show price and cost basis
                const formattedValue = `$${formatDollar(value)}`;
                const basisValue =
                  pointData && pointData.basis != null ? `$${formatDollar(pointData.basis)}` : null;

                if (name === 'Price') {
                  return [formattedValue, selectedTicker || 'Price'];
                } else if (name === 'Cost Basis' && basisValue !== null) {
                  return [basisValue, 'Cost Basis'];
                }
              }
              return [null, null];
            }}
          />
          <Line 
            type="monotone" 
            dataKey="close" 
            name={isDol2View ? "Cumulative PnL" : "Price"} 
            stroke="#22C55E" 
            strokeWidth={2} 
            dot={false}
            activeDot={false}
          />
          {/* Cost Basis line - only show in dol1 (price) view, not in dol2 (cumulative PnL) view */}
          {/* Render before trading activity markers so icons appear on top */}
          {!isDol2View && (
            <Line
              type="stepAfter"
              dataKey="basis"
              name="Cost Basis"
              stroke="#8884d8"
              strokeDasharray="5 5"
              dot={false}
              activeDot={false}
              connectNulls={false}
            />
          )}
          <Line
            type="monotone"
            dataKey="close"
            stroke="transparent"
            strokeWidth={0}
            activeDot={false}
            dot={(props: any) => (
              <TradingActivityMarker
                cx={props.cx}
                cy={props.cy}
                payload={props.payload}
                originalDateStr={new Date(props.payload?.time || 0).toISOString().split('T')[0]}
                activities={activitiesData}
                groupingMs={groupingMs}
                onMarkerClick={onActivityMarkerClick}
                ticker={selectedTicker || undefined}
                view={isDol2View ? 'dol2' : 'dol1'}
                chartDataPoints={chartData.map(d => d.time)}
              />
            )}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderPercentChart = (label: string, color: string, activitiesData: TradingActivitiesData) => {
    if (isFetching && chartData.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full space-y-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <span className="text-sm text-gray-500">Loading data...</span>
        </div>
      );
    }
    if (chartData.length === 0) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          No {label.toLowerCase()} performance data available for the selected period.
        </div>
      );
    }

    const currentView = view || 'pct';
    const isDollarView = currentView === 'dollar';
    // For dollar view, don't add zero line (account values are always positive)
    // For percent view, add zero line for reference
    const dataWithZeroLine = isDollarView 
      ? chartData.map((point) => ({ ...point }))
      : chartData.map((point) => ({ ...point, zeroLine: 0 }));

    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={dataWithZeroLine}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
          <XAxis
            type="number"
            dataKey="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={formatTime}
            axisLine={false}
            tickLine={false}
            stroke="#6B7280"
          />
          <YAxis
            domain={['auto', 'auto']}
            axisLine={false}
            tickLine={false}
            stroke="#6B7280"
            width={60}
            tickFormatter={(value) => isDollarView ? formatDollarAxis(value) : `${value.toFixed(1)}%`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              border: '1px solid #ccc',
              borderRadius: '4px',
            }}
            itemStyle={{ color: '#333' }}
            labelFormatter={(timestamp) => {
              const date = new Date(timestamp);
              const month = date.toLocaleString('default', { month: 'short' });
              const day = date.getDate();
              if (timeWindowKey === '1y' || timeWindowKey === '2y') {
                const year = date.getFullYear();
                return `${month} ${day}, ${year}`;
              }
              return `${month} ${day}`;
            }}
            formatter={(value: number, name: string) => {
              if (name === 'zeroLine' || name === 'tradingActivity' || name === '') return [null, null];
              if (isDollarView) {
                // $ view: Show dollar value with commas
                const displayLabel = label === 'Account Performance' ? 'Account Value' : 'Portfolio Value';
                const formattedValue = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                return [`$${formattedValue}`, displayLabel];
              } else {
                // % view: Show percentage value
                return [`${value.toFixed(2)}%`, label];
              }
            }}
          />
          <Line 
            type="monotone" 
            dataKey="percent" 
            name={isDollarView ? (label === 'Account Performance' ? 'Account Value' : 'Portfolio Value') : label} 
            stroke={color} 
            strokeWidth={2} 
            dot={false}
            activeDot={false}
          />
          {!isDollarView && (
            <Line
              type="monotone"
              dataKey="zeroLine"
              name="zeroLine"
              stroke="#8884d8"
              strokeDasharray="5 5"
              dot={false}
              activeDot={false}
              connectNulls={false}
            />
          )}
          <Line
            type="monotone"
            dataKey="percent"
            name="tradingActivity"
            stroke="transparent"
            strokeWidth={0}
            activeDot={false}
            dot={(props: any) => (
              <TradingActivityMarker
                cx={props.cx}
                cy={props.cy}
                payload={props.payload}
                originalDateStr={new Date(props.payload?.time || 0).toISOString().split('T')[0]}
                activities={activitiesData}
                groupingMs={groupingMs}
                onMarkerClick={onActivityMarkerClick}
                view={isDollarView ? 'dollar' : 'pct'}
                chartDataPoints={chartData.map(d => d.time)}
              />
            )}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderChart = () => {
    if (chartType === 'ticker') {
      return renderTickerChart();
    } else if (chartType === 'account') {
      return renderPercentChart('Account Performance', '#79C9CE', accountActivities || {});
    } else if (chartType === 'portfolio') {
      return renderPercentChart('Portfolio Performance', '#AEAEAE', portfolioActivities || {});
    } else {
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          Select an investment to view its history.
        </div>
      );
    }
  };

  return (
    <div className={`bg-white rounded-lg p-6 shadow-sm ${className}`}>
      <div className="flex justify-between items-center mb-4 gap-4">
        <div className="flex flex-col min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-green-700 break-words">{title}</h2>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          {/* Hide toggle for Top Mover tickers (no selectedAccountId) or friend portfolio */}
          {!hideToggle && !(chartType === 'ticker' && !selectedAccountId) && (
            <button
              onClick={handleToggle}
              className="p-1 rounded bg-transparent hover:bg-transparent flex-shrink-0"
              title={chartType === 'ticker' ? 'Toggle price / cumulative PnL view' : 'Toggle $ / % view'}
              aria-label={toggleAltText}
            >
              <img
                src={toggleImage}
                alt={toggleAltText}
                className="w-10 h-auto"
              />
            </button>
          )}
        </div>
      </div>
      <div className="w-full h-[300px]">{renderChart()}</div>
    </div>
  );
};

